import React, { Component } from 'react'
import { Row, Col, Modal, Dropdown } from 'react-bootstrap';
import { inject, observer } from 'mobx-react';
import moment from 'moment';

//components
import { ViewTypes } from '../../../components/Scheduler';
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import withDragDropContext from '~/hoc/withDnDContext';
import RosterShiftView from './RosterShiftView';
import Scheduler, { SchedulerData } from './RosterScheduler';
import DownloadExcelModal from './DownloadExcelModal';
import PublishTemplateModal from './PublishTemplateModal';
import F5PlacementsModal from './F5PlacementsModal';

//elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import Select from '../../../elements/Select';
import LoadingSpinner from '~/elements/LoadingSpinner';
import SwitchWidget from '../ControlPanel/SwitchWidget';
import { CurrentUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget';

//utils
import { timeFormat } from '~/utils/dateFormat';
import { validateModuleAccess } from '../../../library/stores/userStore';

//assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';
import flagIcon from '../../../assets/img/flag.svg';

//styles
import './RosterScheduler/css/style.css';

const cellEventItemHeight = 40;

const departmentOptions = (departments) => {
    const formatValues = departments.map(item => ({ value: item.code, label: item.name }))
    formatValues.push({ value: 'z_none', label: 'Others' })
    return formatValues
}

const filterResources = (list, { department, user_group, field, value }, rosterStore, groupDetails) => {
    let filtered = list.slice(1);

    switch (field) {
        case 'department':
            if (value)
                filtered = filtered.filter(r => r.employee_type === value &&
                    (user_group > 0 ? !!groupDetails.find(g => g.code === user_group && g.userIds.includes(r.id)) : true)
                );
            else
                filtered = filtered.filter(r => (user_group > 0 ? !!groupDetails.find(g => g.code === user_group && g.userIds.includes(r.id)) : true));
            break;

        case 'user_group':
            if (value > 0)
                filtered = filtered.filter(r => (!!groupDetails.find(g => g.code === value && g.userIds.includes(r.id))) &&
                    (department ? r.employee_type === department : true)
                );
            else
                filtered = filtered.filter(r => (department ? r.employee_type === department : true));
            break;

        case 'status':
            if (user_group > 0)
                filtered = filtered.filter(r => (user_group > 0 ? !!groupDetails.find(g => g.code === user_group && g.userIds.includes(r.id)) : true) &&
                    (department ? r.employee_type === department : true)
                );
            break;

        default:
            break;
    }

    if (rosterStore?.filterResources?.length) {
        filtered = filtered.filter(r => rosterStore.filterResources.includes(r.id));
    }

    return filtered;
};

const statusOptions = (t, allow_union_approval, draft = 0, active = 0, approved = 0, rejected = 0, showCount = true) => {
    const options = [
        { value: '', label: t('All') },
        { value: 'published', label: t('Published') },
        { value: 'draft', label: `${t('Draft')}${showCount ? `: ${draft}` : ''}` },
        { value: 'active', label: `${t('Active')}${showCount ? `: ${active}` : ''}` },
    ]
    if (allow_union_approval) {
        options.push({ value: 'approved', label: `${t('Approved')}${showCount ? `: ${approved}` : ''}` })
        options.push({ value: 'rejected', label: `${t('Rejected')}${showCount ? `: ${rejected}` : ''}` })
    }
    return options;
}

const isResourcePlannerAllowed = (commonStore) => {
    const { allow_resource_planner } = commonStore.config?.client?.data.basicRules?.workPlanSettings;
    return allow_resource_planner;
}

class RosterScheduler extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            events: [],
            status: '',
            byClock: false,
            viewModel: null,
            width: 0,
            userId: null,
            resources: [],
            department: null,
            user_group: -1,
            showRosterShiftViewModal: false,
            showSendToUnionModal: false,
            showPublishModal: false,
            showDownloadExcelModal: false,
            showF5PlacementsModal: false,
            departmentWiseStatusCounts: null,
            departmentWiseCycleDetails: null,
            userExceptionDetails: null,
            groupDetails: [],
            skillDetails: [],
            showDownloadExcelModal: false,
            // currentView: 'view',
            allocatedResourcePlan: null
        };
        this.scheduleRef = React.createRef();
    }

    componentDidMount() {
        if (!validateModuleAccess('md_Roster', 'view')) {
            this.setState({ isLoading: false });
            return;
        }
        this.loadData();
        if (window.innerWidth < 1900)
            this.props.handleMiniClick();
        window.addEventListener('resize', this.updateWidth);
    }

    componentDidUpdate(prevProps, prevState) {
        if ((prevState.isLoading && !this.state.isLoading) || (prevProps.hideCollapseView !== this.props.hideCollapseView))
            this.updateWidth();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWidth);
    }

    updateWidth = () => {
        const hideSidebar = this.props.hideCollapseView ? window.innerWidth - 80 - 75 : window.innerWidth - 260 - 75;
        if (this.scheduleRef.current)
            this.setState({ width: hideSidebar });
    };

    loadData = async () => {
        const { scheduleStore, rosterStore, userGroupStore, userStore, resourcePlanStore } = this.props;
        const { department, user_group, status } = rosterStore.filterValues;
        const { viewModel } = this.state;
        this.setState({ loading: true, department, user_group, status })
        const { currentUser } = userStore;
        const isMember = currentUser.user_type === 'member';
        const effectiveDepartment = isMember ? currentUser.employee_type || null : department ?? null;

        this.setState({ loading: true, department: effectiveDepartment, user_group: user_group !== undefined ? user_group : -1, status });

        if (effectiveDepartment !== department) rosterStore.setFilterValues('department', effectiveDepartment);

        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }

        const [_, groupDetails] = await Promise.all([
            scheduleStore.loadResources({ pmMembers }, true),
            userGroupStore.getGroupDetails({ include_user_details: true }),
        ]);

        let effectiveUserGroup = user_group !== undefined ? user_group : -1;
        if (effectiveUserGroup === -1 && isMember) {
            const userGroup = groupDetails.find(g => g.userIds && g.userIds.includes(currentUser.id));
            if (userGroup) {
                effectiveUserGroup = userGroup.code;
                rosterStore.setFilterValues('user_group', effectiveUserGroup);
            }
        }

        this.setState({ groupDetails, user_group: effectiveUserGroup }, () => this.initRosterScheduler());

    }

    handleChange = async (field, value) => {
        const { rosterStore, commonStore, scheduleStore, t, resourcePlanStore, skillsStore } = this.props;
        const { viewModel, user_group, department, groupDetails } = this.state;
        const schedulerData = viewModel;
        const { resources } = scheduleStore;
        let _skillDetails = [];

        if (value === this.state[field]) return;

        const setResources = (list) => {
            if (!list.length) {
                commonStore.addNotification(t('No employees found for the applied filter'), null, 'error');
                return false;
            }
            schedulerData.setResources(list);
            this.setState({ viewModel: schedulerData });
            return true;
        };

        try {
            if (field === 'status') {
                const jumpDate = rosterStore.shiftDetailsWhenAppliedFilters[value] || 0;
                const diffMonths = jumpDate ? moment(jumpDate).month() - moment(viewModel?.startDate).month() : 0;
                if (diffMonths) schedulerData.customJump(diffMonths);

                const params = {
                    start: viewModel?.startDate,
                    end: viewModel?.endDate,
                    status: value,
                };

                const res = await rosterStore.loadList(params, false, t);
                const _resources = filterResources(resources, { department, user_group, field, value }, rosterStore, groupDetails);
                if (!setResources(_resources)) return;

                schedulerData.setEvents(res.events);
                this.setState({ viewModel: schedulerData });

            } else if (['department', 'user_group'].includes(field)) {
                const _resources = filterResources(resources, { department, user_group, field, value }, rosterStore, groupDetails);
                if (!setResources(_resources)) return;
                if (isResourcePlannerAllowed(commonStore) && field === 'department' && value) {
                    await resourcePlanStore.dateWiseCount({ department: value, start_date: schedulerData?.startDate, end_date: schedulerData?.endDate });
                    _skillDetails = await skillsStore.getSkillUserDetails({});
                }
                else
                    resourcePlanStore.clearPlanCount();
            }

            rosterStore.setFilterValues(field, value);
            this.setState({ [field]: value, skillDetails: _skillDetails });
        } catch (err) {
            console.error('Error in handleChange:', err);
            commonStore.addNotification(t('An error occurred while applying filter'), null, 'error');
        }
    };

    initRosterScheduler = async (_newSchedule) => {
        const { t, scheduleStore, rosterStore, skillsStore, resourcePlanStore, commonStore } = this.props;
        const { user_group, department, groupDetails } = this.state;
        const { resources } = scheduleStore;
        this.setState({ isLoading: true })
        const schedulerData = _newSchedule ? _newSchedule : new SchedulerData(
            moment().startOf('month').format('YYYY-MM-DD'),
            ViewTypes.Month,
            false,
            false,
            {
                startResizable: false,
                endResizable: false,
                movable: false,
                resourceName: t('Employee name'),
                nonAgendaOtherCellHeaderFormat: 'ddd | D',
                schedulerWidth: '100%',
                tableHeaderHeight: 75,
                eventItemHeight: cellEventItemHeight,
                eventItemLineHeight: cellEventItemHeight,
                yearCellWidth: 60,

            },
        );
        schedulerData.localeMoment.locale('en');
        schedulerData.commonStore = commonStore;
        const params = {
            start: schedulerData?.startDate,
            end: schedulerData?.endDate,
            status: this.state.status
        };
        const [res, _, skillDetails] = await Promise.all([
            rosterStore.loadList(params, false, t),
            isResourcePlannerAllowed(commonStore) && department ? resourcePlanStore.dateWiseCount({ department, start_date: schedulerData?.startDate, end_date: schedulerData?.endDate }) : Promise.resolve(),
            isResourcePlannerAllowed(commonStore) && department ? skillsStore.getSkillUserDetails({}) : Promise.resolve([])
        ]);

        let _resource = resources.slice(1);
        if (rosterStore?.filterResources?.length)
            _resource = _resource.filter((r) => rosterStore?.filterResources.includes(r.id));

        _resource = _resource.filter(r => (user_group > 0 ? (!!groupDetails.find(g => g.code === user_group && g.userIds.includes(r.id))) : true) &&
            (department ? r.employee_type === department : true)
        );

        schedulerData.setResources(_resource);
        schedulerData.setEvents(res.events);
        this.setState({
            viewModel: schedulerData, departmentWiseStatusCounts: res.departmentWiseStatusCounts, resources: _resource,
            departmentWiseCycleDetails: res.workHoursRules, userExceptionDetails: res.resourceException, skillDetails
        }, () => {
            this.setState({ isLoading: false })
        })
    }

    customEventItemTemplate = (_, eventItem) => {
        const dateTimeRules = this.props.commonStore.config.client.data.dateTimeRules;
        if (this.state.byClock)
            return (
                <div
                    className='fw-600 text-dark text-center'
                    style={{ backgroundImage: eventItem.isPublishedAndEdited ? `url(${flagIcon})` : 'none', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: '20px' }}
                >
                    <div style={{ fontSize: '10px' }}>{eventItem.title}</div>
                    <div style={{ fontSize: '8px' }}>{timeFormat(eventItem.startTime, dateTimeRules)}{(' to')}</div>
                    <div style={{ fontSize: '8px' }}>{timeFormat(eventItem.endTime, dateTimeRules)}</div>
                </div>
            )

        return (<div
            className={`roster-schedule-event border-radius-10 text-center ${/f[1-5]/i.test(eventItem.title) ? `roster-freeCode` : eventItem.isStandby ? `roster-standby` : `roster-${eventItem.status}`}`}
            style={{
                backgroundImage: eventItem.isPublishedAndEdited ? `url(${flagIcon})` : 'none',
                height: `${cellEventItemHeight}px`
            }}
        >
            {eventItem.isStandby ? <i class="fas fa-moon" style={{ color: '#2550AC' }}></i> : <></>}
            <div className='fs12'>{eventItem.additionalFCode ? `${eventItem.title} (${eventItem.additionalFCode})` : eventItem.title}</div>
        </div>
        );
    };

    handleResourceOptionClick = (slotId, action) => {
        if (action === 'addNewTemplate') {
            if (!validateModuleAccess('md_Roster', 'createAndEdit')) return;
            this.props.router.navigate('/admin/roster/add', { state: { userId: slotId, add: true } });
        } else if (action === 'setExistingTemplate')
            this.setState({ showRosterShiftViewModal: true, userId: slotId })
        else if (action === 'navigateToException') {
            if (!validateModuleAccess('md_Roster', 'createAndEdit')) return;
            const date = this.state && this.state.userExceptionDetails[slotId] ? this.state.userExceptionDetails[slotId] : ''
            if (date) this.props.router.navigate('/admin/roster/add', { state: { userId: slotId, eventDate: date } });
        }
    };

    prevClick = async (schedulerData) => {
        schedulerData.prev();
        this.initRosterScheduler(schedulerData)
    }

    nextClick = async (schedulerData) => {
        schedulerData.next();
        this.initRosterScheduler(schedulerData)
    }

    handleNavigation = () => {
        this.props.handleCrossClick();
        setTimeout(() => {
            this.props.router.navigate('/admin/schedule');
        }, 500);
    }

    newEvent = (_, slotId, slotName, start, end, type, item) => {
        if (!validateModuleAccess('md_Roster', 'createAndEdit')) return;
        const date = start.split(' ')[0]
        this.props.router.navigate('/admin/roster/add', { state: { userId: slotId, eventDate: date } });
    }

    eventClicked = (_, event) => {
        if (!validateModuleAccess('md_Roster', 'createAndEdit')) return;
        this.props.router.navigate('/admin/roster/add', { state: { userId: event.resourceId, eventDate: event.start, event } });
    };

    handleSentForApproval = async () => {
        const { department } = this.state;
        const { rosterStore, commonStore, t } = this.props;
        const res = await rosterStore.sendRosterForApproval({ department });
        this.setState({ showSendToUnionModal: false });
        commonStore.addNotification(t(res.message), null, 'success');
    }

    render() {
        const { byClock, viewModel, department, isLoading, width, status, showRosterShiftViewModal, userId, departmentWiseStatusCounts, showPublishModal,
            user_group, departmentWiseCycleDetails = null, userExceptionDetails, showDownloadExcelModal, groupDetails, skillDetails, showF5PlacementsModal } = this.state;
        const { commonStore, userStore, t, resourcePlanStore } = this.props;
        const dateTimeRules = commonStore.getDateTimeRules();
        const { allow_union_approval, allow_resource_planner } = commonStore.config?.client?.data.basicRules?.workPlanSettings;

        let _departmentOptions = departmentOptions(commonStore?.config?.client?.data?.roles)
        _departmentOptions = _departmentOptions.map(department => {
            const active = departmentWiseStatusCounts?.[department.value]?.active || 0;
            return {
                ...department,
                active,
                label: department.label
            };
        });
        if (userStore.currentUser.user_type === 'member') {
            _departmentOptions = _departmentOptions.filter(o => o.value === userStore.currentUser.employee_type);
        }

        if (isLoading) return <LoadingSpinner />;

        if (!validateModuleAccess('md_Roster', 'view')) {
            return (
                <div className="text-center mt-5 text-muted">
                    <i className="fas fa-lock fa-2x mb-3 d-block"></i>
                    {t('You do not have access to the Roster module.')}
                </div>
            );
        }

        const canCreateAndEdit = validateModuleAccess('md_Roster', 'createAndEdit');
        const canManageActive = validateModuleAccess('md_Roster', 'manageActiveTemplate');
        const canPublish = validateModuleAccess('md_Roster', 'publish');
        const canDeletePublished = validateModuleAccess('md_Roster', 'deletePublished');
        const canManageWorkPattern = validateModuleAccess('md_WorkPattern', 'manageWorkPatterns');
        const canViewWorkPattern = validateModuleAccess('md_WorkPattern', 'view');

        const draftTemplatesCount = Object.values(departmentWiseStatusCounts).reduce((sum, department) => sum + department.draft, 0);
        const activeTemplatesCount = Object.values(departmentWiseStatusCounts).reduce((sum, department) => sum + department.active, 0);
        const approvedTemplatesCount = Object.values(departmentWiseStatusCounts).reduce((sum, department) => sum + department.approved, 0);
        const rejectedTemplatesCount = Object.values(departmentWiseStatusCounts).reduce((sum, department) => sum + department.rejected, 0);
        const _statusOptions = statusOptions(t, allow_union_approval, draftTemplatesCount, activeTemplatesCount, approvedTemplatesCount, rejectedTemplatesCount, userStore.currentUser.user_type !== 'member');

        return (
            <div ref={this.scheduleRef} className='roster-scheduler-container'>
                <Row>
                    <Col>
                        <span className='fw-600 custom-customer-header'>{t('Roster')}</span>
                    </Col>
                </Row>
                <Row className='mt-2'>
                    <Col md={4}>
                        <Row>
                            <Col>
                                <label className="control-label mb-1">{t('Department')}</label>
                                <Select
                                    placeholder={t('Select...')}
                                    options={_departmentOptions}
                                    value={_departmentOptions.find(ele => ele.value === department)}
                                    onChange={e => this.handleChange('department', e?.value)}
                                    isClearable={true}
                                />
                            </Col>
                            <Col>
                                <label className="control-label mb-1">
                                    {`${t('Select')} ${t('Group')}`}
                                </label>
                                <CurrentUserGroupsWidget
                                    value={user_group}
                                    onChange={value => this.handleChange('user_group', value)}
                                    user_id={userStore?.currentUser?.user_type === 'member' ? userStore.currentUser.id : 0}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col md={2}>
                        <label className="control-label mb-1">
                            {`${t('Select')} ${t('Status')}`}
                        </label>
                        <Select
                            placeholder={t('Select...')}
                            options={_statusOptions}
                            value={_statusOptions.find(ele => ele.value === status)}
                            onChange={e => this.handleChange('status', e?.value)}
                        />
                    </Col>
                    <Col md={6} className='d-flex align-items-center justify-content-end gap-4'>
                        <div className='d-flex flex-row'>
                            <SwitchWidget className='mt-1 me-1'
                                value={byClock}
                                onChange={checked => this.handleChange('byClock', checked)}
                            />
                            <span className='fs12 d-flex align-items-center'>{t('Clock View')}</span>
                        </div>
                        <div className='d-flex flex-column align-items-start justify-content-start p-0'>
                            <div>
                                <i className="fas fa-circle" style={{ color: '#f2ff9c', border: '1px solid black' }}></i>
                                <span className='ml-10 fw-600 fs12'>{t('Active')}</span>
                            </div>
                            <div>
                                <i className="fas fa-circle" style={{ color: '#d0e7f7', border: '1px solid black' }}></i>
                                <span className='ml-10 fw-600 fs12'>{t('Published')}</span>
                            </div>
                            <div>
                                <i className="fas fa-circle" style={{ color: '#FFE4B5', border: '1px solid black' }}></i>
                                <span className='ml-10 fw-600 fs12'>{t('Holiday')}</span>
                            </div>
                        </div>
                        <Button onClick={() => this.handleNavigation()}>
                            {t('Schedule')}
                        </Button>
                    </Col>
                </Row>

                <Row className='mt-2 d-flex justify-content-between' style={{ marginBottom: '-40px' }}>
                    <Col md={5}>
                        <div className='d-flex' style={{ width: this.props.hideCollapseView ? '75%' : '86%', justifyContent: allow_union_approval && activeTemplatesCount ? 'space-between' : 'start', gap: allow_union_approval && activeTemplatesCount ? 0 : '10px' }}>
                            {(canManageActive || canDeletePublished) && <Button md onClick={() => this.handleResourceOptionClick(userStore?.currentUser.id, 'setExistingTemplate')}>{t('Roster template')}</Button>}
                            {canPublish && <Button md onClick={() => this.setState({ showPublishModal: true })}>{t('Publish')}</Button>}
                            {allow_union_approval && activeTemplatesCount && userStore?.currentUser?.user_type !== 'member' ? <Button md onClick={() => this.setState({ showSendToUnionModal: true })} disabled={!activeTemplatesCount}>{t('Request Approval')} ({activeTemplatesCount})</Button> : <></>}
                            {validateModuleAccess('md_QuickPlanner', 'view') && <Button md onClick={() => this.props.router.navigate('/admin/roster/quick-planner', { state: { userGroup: user_group, department } })}>{t('Quick Planner')}</Button>}
                        </div>
                    </Col>
                    <Col md={7} className='d-flex justify-content-end'>
                        {userStore?.currentUser?.user_type !== 'member' &&
                            <Button md fill onClick={() => this.setState({ showDownloadExcelModal: true })}>{t('Report')}</Button>
                        }
                        {userStore?.currentUser?.user_type !== 'member' &&
                            <div className='ms-2'>
                                <Button md onClick={() => this.setState({ showF5PlacementsModal: true })}>{t('F5 Placements')}</Button>
                            </div>
                        }
                        {(canViewWorkPattern || canManageWorkPattern) && (
                            <div className='custom-style-userlist me-2 ms-2'>
                                <Dropdown className="d-inline">
                                    <Dropdown.Toggle id="dropdown-autoclose-inside">
                                        {t('Automation')}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu renderOnMount align="end" popperConfig={{ strategy: 'fixed' }}>
                                        {canManageWorkPattern && (
                                            <Dropdown.Item
                                                onClick={() => { this.props.router.navigate('/admin/roster/work-pattern', { state: { userGroup: user_group, department } }) }}>
                                                <div>{t('Create')} {t('Work Pattern')}</div>
                                            </Dropdown.Item>
                                        )}
                                        {canManageWorkPattern && (
                                            <Dropdown.Item
                                                onClick={() => { this.props.router.navigate('/admin/roster/work-pattern-assignment') }}>
                                                <div>{t('Assign Template')}</div>
                                            </Dropdown.Item>
                                        )}
                                        {canViewWorkPattern && (
                                            <Dropdown.Item
                                                onClick={() => { this.props.router.navigate('/admin/roster/work-pattern-assignment', { state: { activeTab: 'assignments' } }) }}>
                                                <div>{t('Assigned Templates')}</div>
                                            </Dropdown.Item>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        )}
                        {allow_resource_planner && department && userStore.currentUser.user_type !== 'member' &&
                            <Button md fill onClick={() => this.props.router.navigate('/admin/roster/resource-planner', { state: { department } })}>{t('Resource Planner')}</Button>
                        }
                    </Col>
                </Row>

                {viewModel &&
                    <div id='Roster-Scheduler' className='mt-4'>
                        <Scheduler
                            width={width}
                            department={department}
                            userGroup={user_group}  // Only to trigger re-render
                            status={status}         // Only to trigger re-render
                            departmentWiseCycleDetails={departmentWiseCycleDetails}
                            schedulerData={viewModel}
                            prevClick={this.prevClick}
                            nextClick={this.nextClick}
                            eventItemTemplateResolver={this.customEventItemTemplate}
                            showResourcesThreeDots
                            canCreateAndEdit={canCreateAndEdit}
                            handleResourceOptionClick={this.handleResourceOptionClick}
                            dateTimeRules={dateTimeRules}
                            newEvent={this.newEvent}
                            eventItemClick={this.eventClicked}
                            userExceptionDetails={userExceptionDetails}
                            t={t}
                            // currentView={currentView}
                            allow_resource_planner={allow_resource_planner}
                            planCount={resourcePlanStore.departmentWisePlanCount || 0}
                            groupDetails={groupDetails}
                            skillDetails={skillDetails}
                            isMainRosterScheduler
                        />
                    </div>
                }

                {userId &&
                    <RosterShiftView
                        show={showRosterShiftViewModal}
                        userId={userId}
                        handleClose={() => this.setState({ showRosterShiftViewModal: false, userId: null })}
                        reload={() => this.loadData()}
                        groupDetails={this.state.groupDetails}
                        canManageActive={canManageActive}
                        canDeletePublished={canDeletePublished}
                    />
                }

                {showDownloadExcelModal &&
                    <DownloadExcelModal
                        show={showDownloadExcelModal}
                        handleClose={() => this.setState({ showDownloadExcelModal: false })}
                        reload={() => this.loadData()}
                    />
                }

                {showF5PlacementsModal &&
                    <F5PlacementsModal
                        show={showF5PlacementsModal}
                        handleClose={() => this.setState({ showF5PlacementsModal: false })}
                        rosterStore={this.props.rosterStore}
                    />
                }

                {showPublishModal &&
                    <PublishTemplateModal
                        show={showPublishModal}
                        close={(isReload) => {
                            this.setState({ showPublishModal: false })
                            isReload && this.loadData()
                        }}
                    />
                }

                <Modal
                    size="md"
                    centered
                    show={this.state.showSendToUnionModal}
                    onHide={() => this.setState({ showSendToUnionModal: false })}
                >
                    <Modal.Header className="set_ModalHeader">
                        <Modal.Title className='w-100 text-center custom-modal-title'>
                            {t('Send For Approval')}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            onClick={() => this.setState({ showSendToUnionModal: false })}
                            alt='close-btn'
                        />
                    </Modal.Header>
                    <Modal.Body style={{ height: 250 }}>
                        <p>{t('Select the department to send the roster you would like to approve for')}</p>
                        <Select
                            placeholder={t('Select...')}
                            options={_departmentOptions}
                            value={_departmentOptions.find(ele => ele.value === department)}
                            onChange={e => this.handleChange('department', e.value)}
                            menuPortalTarget={document.body}
                            classNamePrefix="edit-wp-select"
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button fill md onClick={this.handleSentForApproval}>{t('Send')}</Button>
                        <Button onClick={() => this.setState({ showSendToUnionModal: false })}>{t('Close')}</Button>
                    </Modal.Footer>
                </Modal>
            </div >
        );
    }
}

export default inject('userStore', 'commonStore', 'scheduleStore', 'clientStore', 'rosterStore', 'userGroupStore', 'resourcePlanStore', 'skillsStore')(withLocalization(applicationRouter(withDragDropContext(observer(RosterScheduler)))));
